import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
} from "@mui/material";
import IngredientPriceHistoryTimeline from "./IngredientPriceHistoryTimeline";
import IngredientCategoryGuide from "./IngredientCategoryGuide";

const IngredientFormDrawer = ({
  open,
  onClose,
  formData,
  setFormData,
  onSubmit,
  editing,
  categories,
  units,
  suppliers,
}) => {
  const [internalValueTouched, setInternalValueTouched] = useState(false);
  const [costRawValue, setCostRawValue] = React.useState("");

  const handleChange = (field) => (e) => {
    const value =
      field === "costPerUnit" ? parseFloat(e.target.value) : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field) => (e) => {
    const v = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: v === "" ? "" : Number(v),
    }));
  };

  useEffect(() => {
    if (editing && formData.costPerUnit != null) {
      const cents = Math.round(formData.costPerUnit * 100);
      setCostRawValue(String(cents));
    }
  }, [editing, formData.costPerUnit]);

  const generateInternalValue = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // remove everything except word chars, spaces, hyphens
      .replace(/\s+/g, "-"); // replace spaces with hyphens
  };

  const handleCostInputChange = (e) => {
    const input = e.target.value.replace(/\D/g, ""); // only digits
    setCostRawValue(input);

    const cents = parseInt(input || "0", 10);
    const dollars = cents / 100;

    setFormData((prev) => ({
      ...prev,
      costPerUnit: dollars,
    }));
  };

  // convenience flags
  const unitLower = (formData.unit || "").toLowerCase();
  const needsGramsPerUnit = unitLower === "pcs" || unitLower === "unit";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 640 } } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          {editing ? "Edit Ingredient" : "Add Ingredient"}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <TextField
          fullWidth
          label="Name"
          value={formData.name}
          onChange={(e) => {
            const value = e.target.value;
            setFormData((prev) => ({
              ...prev,
              name: value,
              internalValue: internalValueTouched
                ? prev.internalValue
                : generateInternalValue(value),
            }));
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Internal Value"
          value={formData.internalValue}
          InputProps={{ readOnly: true, sx: { backgroundColor: "#f9f9f9" } }}
          onChange={(e) => {
            setInternalValueTouched(true);
            setFormData((prev) => ({ ...prev, internalValue: e.target.value }));
          }}
          helperText="Automatically generated, can be overridden."
          sx={{ mb: 2 }}
        />

        <TextField
          select
          fullWidth
          label="Category"
          value={formData.category}
          onChange={handleChange("category")}
          sx={{ mb: 2 }}>
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </TextField>

        <IngredientCategoryGuide />

        <TextField
          select
          fullWidth
          label="Unit"
          value={formData.unit}
          onChange={handleChange("unit")}
          sx={{ mb: 2 }}>
          {units.map((u) => (
            <MenuItem key={u} value={u}>
              {u}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Unit Amount (e.g., 100g, 1kg)"
          value={formData.unitAmount}
          onChange={handleChange("unitAmount")}
          sx={{ mb: 2 }}
        />

        {/* NEW: grams per unit (for pcs/unit) */}
        {needsGramsPerUnit && (
          <TextField
            type="number"
            fullWidth
            label="Grams per Unit (pcs)"
            value={formData.gramsPerUnit ?? ""}
            onChange={handleNumberChange("gramsPerUnit")}
            inputProps={{ min: 0, step: "0.1" }}
            helperText="Used for components' auto-yield and pcs↔g conversions (e.g., large egg ≈ 55g)."
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          fullWidth
          label="Cost Per Unit"
          value={
            costRawValue ? (parseInt(costRawValue, 10) / 100).toFixed(2) : ""
          }
          onChange={handleCostInputChange}
          inputMode="numeric"
          sx={{ mb: 2 }}
        />

        <TextField
          select
          fullWidth
          label="Reason for Price Change"
          value={formData.priceChangeReason || ""}
          onChange={handleChange("priceChangeReason")}
          disabled={!editing || formData.costPerUnit === editing.costPerUnit}
          helperText={
            !editing || formData.costPerUnit === editing.costPerUnit
              ? "No price change detected"
              : ""
          }
          sx={{ mb: 2 }}>
          <MenuItem value="price-increase">Price Increase</MenuItem>
          <MenuItem value="price-decrease">Price Decrease</MenuItem>
          <MenuItem value="correction">Correction</MenuItem>
        </TextField>

        <TextField
          select
          fullWidth
          label="Supplier"
          value={
            typeof formData.supplier === "object"
              ? formData.supplier._id
              : formData.supplier
          }
          onChange={handleChange("supplier")}
          sx={{ mb: 2 }}>
          {suppliers.map((sup) => (
            <MenuItem key={sup._id} value={sup._id}>
              {sup.name}
            </MenuItem>
          ))}
        </TextField>

        {(typeof formData.supplier === "string"
          ? suppliers.find((s) => s._id === formData.supplier)?.name
          : formData.supplier?.name) === "Costco" && (
          <TextField
            fullWidth
            type="number"
            label="Store Path Index"
            value={formData.storePathIndex ?? 999}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                storePathIndex: parseInt(e.target.value, 10),
              }))
            }
            sx={{ mb: 2 }}
            helperText="Used to sort items in-store for Costco"
          />
        )}

        {editing?.priceHistory?.length > 0 && (
          <IngredientPriceHistoryTimeline
            priceHistory={editing.priceHistory}
            unit={editing.unit}
            currentPrice={formData.costPerUnit}
            currentUnitAmount={formData.unitAmount}
          />
        )}

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={onSubmit}>
            {editing ? "Update" : "Create"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default IngredientFormDrawer;
